import { FieldStructureMap } from '../../../service-request-form-response.mapper.util';
import {
  buildChecklistField,
  buildGeneralInformationFieldStructure,
  buildTechnicianSignatureFieldStructure,
} from '../shared/static-form-fields.util';
import { TechnicianSignatureLayoutEntry } from '../shared/static-form-section.util';

const field = (
  controlKey: string,
  label: string,
  sequence: number,
  overrides: Partial<FieldStructureMap[string]> = {},
): [string, FieldStructureMap[string]] => [
  controlKey,
  {
    type: overrides.type ?? 'TEXT',
    label,
    sequence,
    required: overrides.required ?? false,
    ...overrides,
  },
];

/** PP-DISP General Information omits LSD per Avenir 2.0 Dispenser Inspection Form design. */
export const PP_DISP_GENERAL_INFO_RIGHT_KEYS = [
  'customerAccount',
  'shipToLocation',
  'shipToCoordinates',
  'shipToLastInspection',
] as const;

export const PP_DISP_EQUIPMENT_OPTIONS = [
  { label: '058693769 - 125 USWG Propane Tank', value: '058693769 - 125 USWG Propane Tank' },
  { label: '058693770 - 250 USWG Propane Tank', value: '058693770 - 250 USWG Propane Tank' },
];

export const PP_DISP_MODE_OF_OPERATION_TYPE_ANOTHER = 'Type Another Option';

export const PP_DISP_MODE_OF_OPERATION_OPTIONS = [
  { label: 'Cylinder Fill', value: 'Cylinder Fill' },
  { label: 'Auto Fill', value: 'Auto Fill' },
  { label: 'Cylinder/Auto Fill', value: 'Cylinder/Auto Fill' },
  { label: 'Type Another Option', value: PP_DISP_MODE_OF_OPERATION_TYPE_ANOTHER },
] as const;

export const PP_DISP_MODE_OF_OPERATION_PRESET_VALUES = new Set(
  PP_DISP_MODE_OF_OPERATION_OPTIONS
    .map((option) => option.value)
    .filter((value) => value !== PP_DISP_MODE_OF_OPERATION_TYPE_ANOTHER),
);

export const PP_DISP_INSPECTION_LEFT_FIELD_ENTRIES: [string, { readOnly?: boolean }][] = [
  ['inspectionNo', { readOnly: true }],
  ['inspectionType', { readOnly: true }],
  ['inspectionDate', {}],
];

export const PP_DISP_INSPECTION_RIGHT_FIELD_ENTRIES: [string, { type?: 'text' | 'select' | 'textarea'; required?: boolean }][] = [
  ['inspectionOutcome', { type: 'select', required: true }],
  ['outcomeRemarks', { type: 'textarea', required: true }],
];

export const PP_DISP_INSPECTION_OUTCOME_OPTIONS = [
  { label: 'Pass', value: 'Pass' },
  { label: 'Fail', value: 'Fail' },
];

export const PP_DISP_EQUIPMENT_LEFT_FIELD_ENTRIES: [string, { readOnly?: boolean }][] = [
  ['serialNo', { readOnly: true }],
  ['equipmentServiceType', { readOnly: true }],
  ['assetType', { readOnly: true }],
  ['assetSubType', { readOnly: true }],
  ['owner', { readOnly: true }],
];

export const PP_DISP_EQUIPMENT_POST_GRID_FIELD_ENTRIES: [string, { type?: 'text' | 'date' | 'select' }][] = [
  ['modeOfOperation', { type: 'select' }],
  ['facilityTssaLicenseNo', {}],
  ['licenseExpiryDate', { type: 'date' }],
];

export const PP_DISP_EQUIPMENT_RIGHT_FIELD_ENTRIES: [string, { readOnly?: boolean }][] = [
  ['manufacturer', { readOnly: true }],
  ['manufactureDate', { readOnly: true }],
  ['tankSize', { readOnly: true }],
  ['crn', { readOnly: true }],
  ['prvDate', { readOnly: true }],
];

export const PP_DISP_EQUIPMENT_PHOTO_SLOTS = [
  { controlKey: 'tssaLicenseImage', label: 'TSSA License Image' },
  { controlKey: 'tankRatingPlate', label: 'Tank Rating Plate' },
] as const;

export const PP_DISP_PROPANE_TANK_CHECKLIST_KEYS = [
  'pipingMeetsCode',
  'vibrationExpansionProvisions',
  'pipingAboveGradeSupported',
  'aboveGroundPipingPainted',
  'undergroundPipingCorrosionProtected',
  'hydrostaticReliefValves',
  'excessFlowValves',
  'hosesFreeOfLeaks',
  'nozzleGoodCondition',
  'propanePipingFreeOfLeaks',
  'tankProtectedFromDamagePiping',
] as const;

export const PP_DISP_PROPANE_TANK_CHECKLIST_FOOTER = {
  notesControlKey: 'propaneTankChecklistNotes',
  photosControlKey: 'propaneTankChecklistPhotos',
  photosMaxPhotoCount: 5,
} as const;

export const PP_DISP_PIPING_CHECKLIST_KEYS = [
  'tankPaintedCondition',
  'gradeBaseRequired',
  'clearanceCombustibles',
  'clearanceAdjacentWalls',
  'clearanceDrains',
  'clearancePropertyLines',
  'clearanceDoorsWindows',
  'clearanceOtherFuelTanks',
  'clearanceUndergroundTanks',
  'tankReliefValvesRainCaps',
  'tankProtectedFromDamageTank',
] as const;

export const PP_DISP_PIPING_CHECKLIST_FOOTER = {
  notesControlKey: 'pipingChecklistNotes',
  notesLabel: 'Inspector Comments',
  photosControlKey: 'pipingChecklistPhotos',
  photosMaxPhotoCount: 5,
} as const;

export const PP_DISP_SAFETY_CHECKLIST_KEYS = [
  'operatingPersonnelTrained',
  'remoteShutoffCondition',
  'breakawayFittingOperational',
  'iscValveCondition',
  'fusibleLinkInPlace',
  'electricalWiringConform',
  'fireExtinguisherAvailable',
  'nonSmokingSignPosted',
  'noIgnitionSourceSignPosted',
  'transportCylinderSecured',
  'scalesMeasurementCanadaCertification',
  'autoDispensersMeasurementCanadaCertification',
] as const;

export const PP_DISP_SAFETY_CHECKLIST_FOOTER = {
  notesControlKey: 'safetyChecklistNotes',
  notesLabel: 'Inspector Comments',
  photosControlKey: 'safetyChecklistPhotos',
  photosMaxPhotoCount: 5,
} as const;

export const PP_DISP_SAFETY_INSERT_AFTER_ITEM_7 = {
  afterItemNumber: 7,
  photoControlKey: 'safetyMidPhotos',
  fields: [{
    controlKey: 'annualFireExtinguisherInspectionDate',
    label: 'Enter Annual Fire Extinguisher Inspection Date',
    type: 'date' as const,
  }],
  showDivider: true,
} as const;

export const PP_DISP_FINAL_EVALUATION_LEFT_FIELD_ENTRIES: [string, { required?: boolean }][] = [
  ['evaluationInspector', { required: true }],
];

export const PP_DISP_FINAL_EVALUATION_RIGHT_FIELD_ENTRIES: [string, { type?: 'textarea'; required?: boolean }][] = [['inspectorCertificateNumber', { type: 'textarea', required: true }]];

export const PP_DISP_FINAL_EVALUATION_CHECKLIST_KEYS = [
  'installationAcceptable',
  'evaluationFollowUpNeeded',
] as const;

export const PP_DISP_FOLLOWUP_LEADING_FIELD_ENTRIES: [string, { type?: 'textarea' | 'date' | 'text'; required?: boolean; fullWidth?: boolean }][] = [['deficiency', { type: 'textarea', fullWidth: true }]];

export const PP_DISP_FOLLOWUP_DATE_FIELD_ENTRIES: [string, { type?: 'textarea' | 'date' | 'text'; fullWidth?: boolean }][] = [
  ['followupInspectionDate', { type: 'date', fullWidth: true }],
  ['complianceTimeframe', { type: 'text', fullWidth: true }],
];

export const PP_DISP_FOLLOWUP_CORRECTION_FIELD_ENTRIES: [string, { type?: 'textarea' | 'date' | 'text'; fullWidth?: boolean }][] = [
  ['followupCorrectiveAction', { type: 'textarea', fullWidth: true }],
  ['deficiencyCorrectedBy', { type: 'text', fullWidth: true }],
  ['deficiencyCorrectedDate', { type: 'date', fullWidth: true }],
];

export const PP_DISP_FOLLOWUP_CHECKLIST_KEYS = ['makingCorrections'] as const;

export const PP_DISP_FOLLOWUP_PHOTO_SLOTS = [
  { controlKey: 'pictureOfRedTag', label: 'Picture of Red Tag' },
  { controlKey: 'correctionPhoto', label: 'Photo of Correction' },
] as const;

export const PP_DISP_SIGNOFF_LAYOUT: TechnicianSignatureLayoutEntry[] = [
  { controlKey: 'technicianName', type: 'userLookup', column: 'left', required: true },
  { controlKey: 'inspectorDesignation', type: 'textarea', column: 'left', required: true, maxLength: 100 },
  { controlKey: 'inspectorRotNumber', type: 'textarea', column: 'left', required: true, maxLength: 100 },
  { controlKey: 'rotExpiryDate', type: 'date', column: 'left', required: true },
  { controlKey: 'serviceDate', type: 'text', column: 'right', readOnly: true },
  { controlKey: 'locationLongitude', type: 'text', column: 'right', readOnly: true },
  { controlKey: 'locationLatitude', type: 'text', column: 'right', readOnly: true },
];

export const PP_DISP_SIGNOFF_FOOTER_FIELD_ENTRIES = [
  ['signOffComments'],
  ['actionTaken'],
] as const;

export const PP_DISP_FOLLOWUP_TECH_SIGNATURE_LAYOUT: TechnicianSignatureLayoutEntry[] = [
  { controlKey: 'technicianName', type: 'text', column: 'left', readOnly: true, label: 'Technician' },
  { controlKey: 'locationLatitude', type: 'text', column: 'left', readOnly: true },
  { controlKey: 'serviceDate', type: 'text', column: 'right', readOnly: true },
  { controlKey: 'locationLongitude', type: 'text', column: 'right', readOnly: true },
];

export const PP_DISP_FOLLOWUP_TECH_SIGNATURE_FOOTER_FIELD_ENTRIES = [
  ['technicianComments'],
] as const;

export const PP_DISP_FIELD_STRUCTURE: FieldStructureMap = Object.fromEntries([
  ...Object.entries(buildGeneralInformationFieldStructure(2)),
  field('inspectionNo', 'Inspection No.', 20, { readOnly: true }),
  field('inspectionType', 'Inspection Type', 21, { readOnly: true }),
  field('inspectionDate', 'Inspection Date', 22, { type: 'DATE' }),
  field('inspectionOutcome', 'Inspection Outcome', 23, {
    type: 'SINGLE',
    required: true,
    subQuestionLabels: ['Pass', 'Fail'],
  }),
  field('outcomeRemarks', 'Outcome Remarks', 24, {
    type: 'TEXT_AREA',
    required: true,
    maxLength: 100,
  }),
  field('equipmentOther', 'Other Equipment', 29, { type: 'TEXT', maxLength: 100 }),
  field('equipment', 'Equipment', 30, {
    type: 'SINGLE',
    required: true,
    subQuestionLabels: PP_DISP_EQUIPMENT_OPTIONS.map((option) => option.label),
  }),
  field('serialNo', 'Serial No.', 31, { readOnly: true }),
  field('equipmentServiceType', 'Service Request Type', 32, { readOnly: true }),
  field('assetType', 'Asset Type', 33, { readOnly: true }),
  field('assetSubType', 'Asset Sub-Type', 34, { readOnly: true }),
  field('owner', 'Owner', 35, { readOnly: true }),
  field('manufacturer', 'Manufacturer', 36, { readOnly: true }),
  field('manufactureDate', 'Manufacture Date', 37, { readOnly: true }),
  field('tankSize', 'Tank Size', 38, { readOnly: true }),
  field('crn', 'CRN', 39, { readOnly: true }),
  field('prvDate', 'PRV Date', 40, { readOnly: true }),
  field('modeOfOperation', 'Mode of Operation', 41),
  field('facilityTssaLicenseNo', 'Facility TSSA License No.', 42),
  field('licenseExpiryDate', 'License Expiry Date', 43, { type: 'DATE' }),
  field('tssaLicenseImage', 'TSSA License Image', 44, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('tankRatingPlate', 'Tank Rating Plate', 45, { type: 'IMAGE', maxPhotoCount: 1 }),
  buildChecklistField(
    'pipingMeetsCode',
    'Does all piping meet minimum requirements of code?',
    50,
  ),
  buildChecklistField(
    'vibrationExpansionProvisions',
    'Have provisions been made for vibration, expansion, contraction?',
    51,
  ),
  buildChecklistField('pipingAboveGradeSupported', 'Is piping above grade properly supported?', 52),
  buildChecklistField('aboveGroundPipingPainted', 'Is above ground pinping and fittings painted?', 53),
  buildChecklistField(
    'undergroundPipingCorrosionProtected',
    'Is underground piping protected from corrosion?',
    54,
  ),
  buildChecklistField(
    'hydrostaticReliefValves',
    'Are hydrostatic relief valves installed on liquid piping where required?',
    55,
  ),
  buildChecklistField('excessFlowValves', 'Are excess flow valves installed where required?', 56),
  buildChecklistField(
    'hosesFreeOfLeaks',
    'Are all hoses free of leaks and in good operating condition?',
    57,
  ),
  buildChecklistField('nozzleGoodCondition', 'Is nozzle in good operating condition?', 58),
  buildChecklistField('propanePipingFreeOfLeaks', 'Is all propane piping free of leaks?', 59),
  buildChecklistField('tankProtectedFromDamagePiping', 'Is tank protected from physical damage', 60),
  field('propaneTankChecklistNotes', 'Notes', 61, {
    type: 'TEXT_AREA',
    isChecklistFooter: true,
    maxLength: 150,
  }),
  field('propaneTankChecklistPhotos', 'Photos', 62, { type: 'IMAGE', maxPhotoCount: 5 }),
  buildChecklistField(
    'tankPaintedCondition',
    'Is tank(s) painted, free from corrosion and damage?',
    70,
  ),
  buildChecklistField('gradeBaseRequired', 'Is grade base under tank(s) are required?', 71),
  buildChecklistField(
    'clearanceCombustibles',
    'Is 10ft area around tank(s) clear of combustibles?',
    72,
  ),
  buildChecklistField(
    'clearanceAdjacentWalls',
    'Are clearances maintained from adjacent combustible/non combustible walls?',
    73,
  ),
  buildChecklistField(
    'clearanceDrains',
    'Are clearances maintained from drains, catch basins, ditches etc.?',
    74,
  ),
  buildChecklistField(
    'clearancePropertyLines',
    'Are clearances maintained from property lines, sources of ignition or open flames?',
    75,
  ),
  buildChecklistField(
    'clearanceDoorsWindows',
    'Are clearances maintained from entrance/exit doors and operable windows?',
    76,
  ),
  buildChecklistField(
    'clearanceOtherFuelTanks',
    'Are clearances maintained from other fuel storage tank(s)?',
    77,
  ),
  buildChecklistField(
    'clearanceUndergroundTanks',
    'Are clearances maintained from underground propane tank(s)?',
    78,
  ),
  buildChecklistField(
    'tankReliefValvesRainCaps',
    'Is tank(s) equipped with relief valves and loose fitting rain caps?',
    79,
  ),
  buildChecklistField('tankProtectedFromDamageTank', 'Is tank protected from physical damage', 80),
  field('pipingChecklistNotes', 'Notes', 81, {
    type: 'TEXT_AREA',
    isChecklistFooter: true,
    maxLength: 150,
  }),
  field('pipingChecklistPhotos', 'Photos', 82, { type: 'IMAGE', maxPhotoCount: 5 }),
  buildChecklistField(
    'operatingPersonnelTrained',
    'Are operating personnel trained and certified as required?',
    90,
  ),
  buildChecklistField('remoteShutoffCondition', 'Is the remote shut off in operating condition?', 91),
  buildChecklistField('breakawayFittingOperational', 'Is the breakaway fitting operational?', 92),
  buildChecklistField('iscValveCondition', 'Is the I.S.C valve in operating condition?', 93),
  buildChecklistField('fusibleLinkInPlace', 'Is there a fusible link in place?', 94),
  buildChecklistField('electricalWiringConform', 'Does the electrical wiring appear to conform?', 95),
  buildChecklistField('fireExtinguisherAvailable', 'Is a 20lb fire extinguisher available?', 96),
  field('annualFireExtinguisherInspectionDate', 'Enter Annual Fire Extinguisher Inspection Date', 97, {
    type: 'DATE',
  }),
  field('safetyMidPhotos', 'Photos', 98, { type: 'IMAGE', maxPhotoCount: 1 }),
  buildChecklistField('nonSmokingSignPosted', 'Is a non smoking sign posted?', 99),
  buildChecklistField('noIgnitionSourceSignPosted', 'Is a no source of ignition sign posted?', 100),
  buildChecklistField(
    'transportCylinderSecured',
    'Is the transport cylinder upright, secured and in a ventilated place?',
    101,
  ),
  buildChecklistField(
    'scalesMeasurementCanadaCertification',
    'SCALES: Has Measurement Canada Certification been completed and in date as required? (2 years)',
    102,
  ),
  buildChecklistField(
    'autoDispensersMeasurementCanadaCertification',
    'AUTO DISPENSERS: Has Measurement Canada certification been completed as required? (Annually)',
    103,
  ),
  field('safetyChecklistNotes', 'Notes', 104, {
    type: 'TEXT_AREA',
    isChecklistFooter: true,
    maxLength: 150,
  }),
  field('safetyChecklistPhotos', 'Photos', 105, { type: 'IMAGE', maxPhotoCount: 5 }),
  field('evaluationInspector', 'Inspector', 110, { type: 'USER_LOOK_UP', required: true }),
  field('inspectorCertificateNumber', 'Inspector Certificate Number', 111, {
    type: 'TEXT_AREA',
    required: true,
    maxLength: 100,
  }),
  buildChecklistField('installationAcceptable', 'Is the installation acceptable?', 112),
  buildChecklistField('evaluationFollowUpNeeded', 'Is an inspection follow up needed?', 113),
  field('deficiency', 'Deficiency', 120, { type: 'TEXT_AREA', maxLength: 100 }),
  field('followupInspectionDate', 'Followup Inspection Date', 121, { type: 'DATE' }),
  field('complianceTimeframe', 'Compliance Timeframe (Days)', 122),
  buildChecklistField('makingCorrections', 'Are you making corrections?', 123),
  field('followupCorrectiveAction', 'Followup/Corrective Action', 124, { type: 'TEXT_AREA', maxLength: 150 }),
  field('deficiencyCorrectedBy', 'Deficiency Corrected By', 125),
  field('deficiencyCorrectedDate', 'Deficiency Corrected Date', 126, { type: 'DATE' }),
  field('pictureOfRedTag', 'Picture of Red Tag', 127, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('correctionPhoto', 'Photo of Correction', 128, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('correctionComments', 'Comments of correction', 129, { type: 'TEXT_AREA', maxLength: 150 }),
  field('followupTechnicianCertificateNumber', 'Technician Certificate Number', 130),
  field('signOffComments', 'Comments', 131, { type: 'TEXT_AREA', maxLength: 150 }),
  field('actionTaken', 'Action Taken', 132, { type: 'TEXT_AREA', maxLength: 150 }),
  field('technicianComments', 'Technician Comments/Remarks', 133, {
    type: 'TEXT_AREA',
    maxLength: 150,
  }),
  ...Object.entries(buildTechnicianSignatureFieldStructure(140)),
]);
